<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AssetImage from './AssetImage.vue'
import { formatNumber, t } from '../core/i18n'
import { livePerformanceNowMs } from '../core/liveClock'
import { preferences } from '../core/preferences'
import {
  projectResourceMicro,
  worldElapsedUsFromPerformanceMs,
} from '../core/resourceProjection'
import type { VillageResource } from '../core/types'
import { resourceLabel, uiText } from '../core/ui'

const props = withDefaults(
  defineProps<{
    resources: VillageResource[]
    villageRevision?: number
    speedBps?: number
  }>(),
  { villageRevision: 0, speedBps: 10_000 },
)

interface DisplayAnchor {
  resource: VillageResource
  receivedAtPerformanceMs: number
}

const anchors = ref<Map<string, DisplayAnchor>>(new Map())
const displayed = ref<Map<string, number>>(new Map())
let authoritativeRevision = props.villageRevision

function resetAnchors(resources: VillageResource[], revision: number): void {
  // Never let an older HTTP response overwrite a newer canonical economy anchor.
  // Mutations advance village revisions monotonically; legitimate spends therefore
  // arrive with a newer revision and are still applied immediately.
  if (
    Number.isSafeInteger(revision) &&
    Number.isSafeInteger(authoritativeRevision) &&
    revision < authoritativeRevision
  )
    return
  const now = typeof performance === 'undefined' ? 0 : performance.now()
  const next = new Map<string, DisplayAnchor>()
  const nextDisplayed = new Map(displayed.value)
  const revisionChanged = revision !== authoritativeRevision
  for (const resource of resources) {
    const key = resource.resourceType.toLowerCase()
    next.set(key, { resource: { ...resource }, receivedAtPerformanceMs: now })
    // A revision change can represent a legitimate spend/refund. In that case the
    // canonical server amount replaces the visual anchor immediately. With the same
    // revision we suppress harmless network-timing jitter only in the display layer.
    const prior = nextDisplayed.get(key)
    nextDisplayed.set(
      key,
      revisionChanged || prior === undefined
        ? resource.amountMicro
        : Math.max(prior, resource.amountMicro),
    )
  }
  authoritativeRevision = revision
  anchors.value = next
  displayed.value = nextDisplayed
}

watch(
  () => [props.resources, props.villageRevision] as const,
  ([resources, revision]) => {
    resetAnchors(resources, revision)
  },
  { immediate: true, deep: true },
)

function projected(resource: VillageResource): number {
  const key = resource.resourceType.toLowerCase()
  const anchor = anchors.value.get(key)
  if (!anchor) return resource.amountMicro
  const elapsedWorldUs = worldElapsedUsFromPerformanceMs(
    livePerformanceNowMs.value - anchor.receivedAtPerformanceMs,
    props.speedBps,
  )
  const value = projectResourceMicro(
    {
      amountMicro: anchor.resource.amountMicro,
      projectionRemainder: anchor.resource.projectionRemainder ?? 0,
      productionRateMicroPerSecond:
        anchor.resource.productionRateMicroPerSecond,
      capacityMicro: anchor.resource.capacityMicro,
    },
    elapsedWorldUs,
  )
  const prior = displayed.value.get(key) ?? value
  const visual = Math.max(prior, value)
  if (visual !== prior) displayed.value.set(key, visual)
  return visual
}

const core = computed(() =>
  props.resources.filter((resource) =>
    ['wood', 'clay', 'iron'].includes(resource.resourceType.toLowerCase()),
  ),
)
const special = computed(() =>
  props.resources.filter(
    (resource) =>
      !['wood', 'clay', 'iron'].includes(resource.resourceType.toLowerCase()),
  ),
)
const sharedCapacity = computed(() => {
  if (core.value.length === 0) return null
  const first = core.value[0]?.capacityMicro
  return core.value.every((resource) => resource.capacityMicro === first)
    ? (first ?? null)
    : null
})
const amount = (micro: number): string => formatNumber(micro / 1_000_000, 0)
const hourly = (microPerSecond: number): string =>
  formatNumber((microPerSecond * 3600) / 1_000_000, 0)
const iconKey = (type: string): string => {
  const key = type.toLowerCase()
  return ['wood', 'clay', 'iron', 'grain', 'population'].includes(key)
    ? key
    : 'ui.icon.coin'
}
</script>

<template>
  <section class="resource-bar" :aria-label="t('resources.title')">
    <div
      v-for="resource in core"
      :key="resource.resourceType"
      class="resource-chip"
    >
      <AssetImage
        class="resource-art"
        :asset-key="iconKey(resource.resourceType)"
        alt=""
      />
      <span class="resource-copy">
        <strong>{{ resourceLabel(resource.resourceType) }}</strong>
        <span class="resource-value">{{ amount(projected(resource)) }}</span>
        <small v-if="preferences.detailed"
          >+{{ hourly(resource.productionRateMicroPerSecond) }}/h</small
        >
      </span>
      <small v-if="sharedCapacity === null" class="resource-limit"
        >/ {{ amount(resource.capacityMicro) }}</small
      >
    </div>
    <div v-if="sharedCapacity !== null" class="resource-chip capacity-chip">
      <AssetImage class="resource-art" asset-key="ui.icon.inventory" alt="" />
      <span class="resource-copy"
        ><strong>{{ uiText('Kapazität', 'Capacity') }}</strong
        ><span class="resource-value">{{ amount(sharedCapacity) }}</span></span
      >
    </div>
    <div
      v-for="resource in special"
      :key="resource.resourceType"
      class="resource-chip special-resource"
    >
      <AssetImage
        class="resource-art"
        :asset-key="iconKey(resource.resourceType)"
        alt=""
      />
      <span class="resource-copy"
        ><strong>{{ resourceLabel(resource.resourceType) }}</strong
        ><span class="resource-value">{{
          amount(projected(resource))
        }}</span></span
      >
    </div>
  </section>
</template>
